import QRCode from 'qrcode';
import { Alpine } from '../../core/alpine-core.js';
import { preferredAppOrigin } from '../../core/preferred-app-origin.js';
import { resolveVenueKioskAppOrigin } from '../../public/kiosk-tablet-api.js';
import { apiFetch } from '../api.js';

function routerNavigate(path) {
  const router = globalThis.PineconeRouter;
  if (router?.navigate) router.navigate(path);
  else globalThis.location.href = path;
}

function parseTrainingLaunchSessionId() {
  const parts = globalThis.location.pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('training');
  if (idx < 0 || !parts[idx + 1]) return '';
  const sid = parts[idx + 1];
  if (sid === 'launch') return '';
  return decodeURIComponent(sid);
}

function formatRuleHint(rule) {
  const r = String(rule || 'double-out');
  if (r === 'straight-out') return 'Straight Out';
  if (r === 'master-out') return 'Master Out';
  return 'Double Out';
}

function templateFormatSummary(format) {
  const f = format || {};
  const start = Number(f.startingScore);
  const label = Number.isFinite(start) ? String(start) : String(f.variant || '501');
  const chk = formatRuleHint(f.checkout);
  const bo = Number(f.bestOfLegs);
  const bon = Number.isFinite(bo) && bo > 0 ? bo : 3;
  return `${label}, ${chk}, Bo${bon}`;
}

Alpine.data('trainingLaunchPage', () => ({
  sessionId: '',
  loading: true,
  busy: false,
  err: '',
  session: null,
  pairSecret: '',
  catalog: null,
  liveStartBusy: false,
  archiveBusy: false,

  trainingListUrl() {
    return '/admin/training';
  },

  resolvedCatalogTemplate() {
    const s = this.session;
    if (!s || !this.catalog?.tiers) return null;
    const tid = String(s.tierId || '');
    const lid = String(s.levelId || '');
    const sidTpl = String(s.sessionTemplateId || '');
    const tier = (this.catalog.tiers || []).find((t) => String(t.id) === tid);
    const level = tier?.levels?.find((l) => String(l.id) === lid);
    const sessionTpl = level?.sessions?.find((x) => String(x.id) === sidTpl);
    if (!sessionTpl) return null;
    return { tier, level, session: sessionTpl };
  },

  formatSummary() {
    const tpl = this.resolvedCatalogTemplate()?.session;
    if (tpl?.format) return templateFormatSummary(tpl.format);
    const f = this.session?.format;
    return f ? templateFormatSummary(f) : '';
  },

  templateDescription() {
    return String(this.resolvedCatalogTemplate()?.session?.description || '').trim();
  },

  venueKioskUrl() {
    const id = String(this.sessionId || '').trim();
    if (!id) return '';
    const origin = resolveVenueKioskAppOrigin();
    return `${origin}/venue-kiosk?trainingSession=${encodeURIComponent(id)}`;
  },

  /** Phone flow: approve then redirect back to venue-kiosk with claim token. */
  kioskApproveQrUrl() {
    const sid = String(this.sessionId || '').trim();
    if (!sid) return '';
    const venueOrigin = resolveVenueKioskAppOrigin();
    const redirect = encodeURIComponent(`${venueOrigin}/venue-kiosk?trainingSession=${encodeURIComponent(sid)}`);
    const approveOrigin = preferredAppOrigin();
    return `${approveOrigin}/training-kiosk-approve.html?sid=${encodeURIComponent(sid)}&redirect=${redirect}`;
  },

  tvPairingHelp() {
    return 'Open the TV page on the wall display, note the pair code, then on the venue tablet (after QR login) enter that code to link the board.';
  },

  canStartLive() {
    const s = this.session;
    if (!s) return false;
    if (String(s.status) === 'completed' || String(s.liveScoring?.state) === 'finished') return false;
    const legs = s.liveScoring?.legs || [];
    if (legs.length > 0) return false;
    return true;
  },

  liveAlreadyStarted() {
    const legs = this.session?.liveScoring?.legs || [];
    return Array.isArray(legs) && legs.length > 0;
  },

  async init() {
    this.sessionId = parseTrainingLaunchSessionId();
    if (!this.sessionId) {
      this.err = 'Missing training session id.';
      this.loading = false;
      return;
    }
    if (!this.$store.features.enabled('training')) {
      this.err = 'Training is disabled for this site.';
      this.loading = false;
      return;
    }
    this.loading = true;
    this.err = '';
    try {
      await Promise.all([this.loadSession(), this.loadCatalog()]);
      await this.$nextTick();
      await this.paintQrCanvas(this.$refs.trainingApproveQr, this.kioskApproveQrUrl());
      await this.paintQrCanvas(this.$refs.trainingVenueKioskQr, this.venueKioskUrl());
    } catch (e) {
      this.err = e?.message || String(e);
    } finally {
      this.loading = false;
    }
  },

  async loadCatalog() {
    try {
      const res = await apiFetch('/training/catalog');
      const j = await res.json().catch(() => ({}));
      this.catalog = res.ok ? j.catalog || null : null;
    } catch {
      this.catalog = null;
    }
  },

  async loadSession() {
    const res = await apiFetch(`/training/sessions/${this.sessionId}`);
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j?.error || 'Could not load session');
    this.session = j.session || null;
    this.pairSecret = String(j.pairSecret || '').trim();
  },

  async paintQrCanvas(canvas, text) {
    if (!canvas || !text) return;
    const size = 168;
    canvas.width = size;
    canvas.height = size;
    await QRCode.toCanvas(canvas, text, {
      width: size,
      margin: 1,
      color: { dark: '#0a0a0a', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    });
  },

  async copyText(label, value) {
    const v = String(value || '').trim();
    if (!v) {
      this.$store?.toastr?.warning?.('Nothing to copy yet.');
      return;
    }
    try {
      await navigator.clipboard.writeText(v);
      this.$store?.toastr?.success?.(`${label} copied`);
    } catch {
      this.$store?.toastr?.error?.('Could not copy to clipboard');
    }
  },

  async startLive() {
    if (!this.canStartLive()) return;
    this.liveStartBusy = true;
    try {
      const res = await apiFetch(`/training/sessions/${this.sessionId}/live/start`, {
        method: 'POST',
        body: { startingRole: 'A' },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || 'Could not start live session');
      this.session = j.session || this.session;
      this.$store?.toastr?.success?.('Live session started — open the scorer when you are ready.');
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.liveStartBusy = false;
    }
  },

  openScorer() {
    routerNavigate(`/admin/training/${this.sessionId}/scorer`);
  },

  backToList() {
    routerNavigate(this.trainingListUrl());
  },

  async archiveAndLeave() {
    if (!globalThis.confirm('Archive this training session? It will disappear from your active list.')) return;
    this.archiveBusy = true;
    try {
      const res = await apiFetch(`/training/sessions/${this.sessionId}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || 'Archive failed');
      }
      this.$store?.toastr?.success?.('Session archived');
      this.backToList();
    } catch (e) {
      this.$store?.toastr?.error?.(e?.message || String(e));
    } finally {
      this.archiveBusy = false;
    }
  },
}));
